import { HSpacer, IconButton, Text, TopAppBar, VSpacer } from '@/components/DesignSystem';
import { Routes } from '@/constants/Routes';
import { useDashboard } from '@/hooks/useDashboard';
import { useDashboardNavigate } from '@/pages/CustomerDashboard/helpers';
import Remove from '@mui/icons-material/Remove';
import AddIcon from '@mui/icons-material/Add';
import ArrowBack from '@mui/icons-material/ArrowBack';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  CircularProgress,
  Container,
  Stack,
  useTheme,
} from '@mui/material';
import { getJsDateFromString } from '@shared/utilities';
import { DateTime } from 'luxon';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useSearchParams } from 'react-router-dom';

interface Faq {
  description: string,
  expanded: boolean,
  title: string,
}

export const FaqPage = () => {
  const { t } = useTranslation();
  const navigateToPage = useDashboardNavigate();
  const theme = useTheme();

  const params = useParams();
  const [searchParams] = useSearchParams();
  const accessToken = searchParams.get('accessToken') ?? params.accessToken;
  const [faqs, setFaqs] = React.useState<Faq[]>([]);

  const { data: dashboardData } = useDashboard(accessToken);
  const brandColor = dashboardData?.retailer.brandColor ?? '#97743a';

  useEffect(() => {
    if (dashboardData?.activeProgram && !faqs.length) {
      const programStartDate = DateTime.fromJSDate(
        getJsDateFromString(dashboardData.activeProgram.startDate),
      );
      const programEndDate = DateTime.fromJSDate(
        getJsDateFromString(dashboardData.activeProgram.endDate),
      );

      const monthsBetween = Math.ceil(programEndDate.diff(programStartDate, 'months').months);

      setFaqs([
        {
          description: t($ => $.customerDashboard.faq.answers.programDescription),
          expanded: false,
          title: t($ => $.customerDashboard.faq.questions.whatIsProgram, {
            programName: dashboardData.activeProgram.name,
          }),
        },
        {
          description: t($ => $.customerDashboard.faq.answers.pointsExpire),
          expanded: false,
          title: t($ => $.customerDashboard.faq.questions.doPointsExpire),
        },
        {
          description: t(
            $ => $.customerDashboard.faq.answers.programPeriodInfo,
            { months: monthsBetween },
          ),
          expanded: false,
          title: t($ => $.customerDashboard.faq.questions.whatToDo),
        },
        {
          description: t($ => $.customerDashboard.faq.answers.statusRollover),
          expanded: false,
          title: t($ => $.customerDashboard.faq.questions.statusRollover),
        },
        {
          description: t($ => $.customerDashboard.faq.answers.reEarnInfo),
          expanded: false,
          title: t($ => $.customerDashboard.faq.questions.reEarnNotification),
        },
        {
          description: t($ => $.customerDashboard.faq.answers.maintainInfo),
          expanded: false,
          title: t($ => $.customerDashboard.faq.questions.maintainStatus),
        },
      ]);
    }
  }, [dashboardData?.activeProgram]);

  if (!dashboardData) {
    return (
      <Container>
        <Stack alignItems="center">
          <VSpacer size="14" />
          <CircularProgress />
        </Stack>
      </Container>
    );
  }

  const ProgramPeriod = () => {
    if (!dashboardData.activeProgram) {
      return null;
    }

    const programStartDate = getJsDateFromString(dashboardData.activeProgram.startDate);
    const programEndDate = getJsDateFromString(dashboardData.activeProgram.endDate);
    return (
      <>
        <VSpacer size="1" />
        <Text category="body-medium" sx={{ color: theme.palette.text.secondary }}>
          {t($ => $.customerDashboard.faq.programPeriod, {
            start: programStartDate,
            end: programEndDate,
          })}
        </Text>
      </>
    );
  };

  const Header = (
    <TopAppBar
      isStatic
      leftAccessory={(
        <IconButton
          onClick={() => navigateToPage(Routes.CUSTOMER_DASHBOARD_HOW_IT_WORKS, accessToken)}
          testID="back-button"
        >
          <ArrowBack />
        </IconButton>
      )}
      rightAccessory={<HSpacer size="7" />}
      testID="top-app-bar"
    />
  );

  const FaqAccordion = ({ faq }: { faq: Faq }) => (
    <Accordion
      disableGutters
      expanded={faq.expanded}
      sx={{
        '&:before': { display: 'none' },
        background: `
          linear-gradient(0deg, rgba(0, 0, 0, 0.02) 0%,
          rgba(0, 0, 0, 0.02) 100%),
          ${theme.palette.background.default}
        `,
        borderRadius: '8px !important',
      }}
      variant="outlined"
    >
      <AccordionSummary
        expandIcon={faq.expanded
          ? <Remove
            onClick={() => {
              setFaqs(faqs.map((f) => (f.title === faq.title ? { ...f, expanded: false } : f)));
            }}
            sx={{ color: brandColor }}
          />
          : <AddIcon
            onClick={() => {
              setFaqs(faqs.map((f) => (f.title === faq.title ? { ...f, expanded: true } : f)));
            }}
            sx={{ color: brandColor }}
          />
        }
      >
        <Stack
          alignItems="center"
          direction="row"
          justifyContent="flex-start"
        >
          <Text category="title-medium">
            {faq.title}
          </Text>
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ paddingTop: 0 }}>
        <Text category="body-medium" sx={{ color: theme.palette.text.secondary }}>
          {faq.description}
        </Text>
      </AccordionDetails>
    </Accordion>
  );

  return (
    <>
      <Stack
        maxWidth="599px"
        minHeight="100vh"
        minWidth="360px"
      >
        {Header}
        <Stack px="16px">
          <Text category="headline-medium">
            {t($ => $.customerDashboard.faq.title)}
          </Text>
          <ProgramPeriod />
          <VSpacer size="7" />
          {faqs.map((faq) => (
            <>
              <FaqAccordion faq={faq} key={faq.title} />
              <VSpacer size="5" />
            </>
          ))}
        </Stack>
      </Stack>
    </>
  );
};
